import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  Badge,
  Box,
  Button,
  Container,
  Group,
  List,
  Loader,
  Modal,
  Paper,
  Progress,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { ArrowLeft, ArrowRight, Clock, CheckCircle2, AlertTriangle, Coffee } from 'lucide-react'
import { RichText } from '../../components/common/RichText'
import { EvaluateResult } from '../../components/common/EvaluateResult'
import { getImageSrc } from '../../utils/imageData'
import { stripLeadingQuestionNumber } from '../../utils/questionText'
import {
  prepareEvaluation,
  submitEvalAnswersBulk,
  submitEvaluation,
  completeModule,
  type EvalQuestionPayload,
  type EvalSummaryResponse,
} from '../../api/evaluations'

/* ------------------------------------------------------------------ */
/*  SAT Timer Constants (real SAT timing)                              */
/* ------------------------------------------------------------------ */
const RW_MINUTES = 64
const BREAK_MINUTES = 10
const MATH_MINUTES = 70
const BREAK_SECONDS = BREAK_MINUTES * 60

const MODULE_ORDER = ['RW-1', 'RW-2', 'M-1', 'M-2']

const PRACTICE_TYPES = ['quiz', 'test', 'enhance']

const PRACTICE_LABEL: Record<string, string> = {
  quiz: 'Quiz',
  test: 'Test',
  mock: 'Mock',
  enhance: 'Enhance',
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */
function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function sectionOf(mod: string): 'RW' | 'Math' {
  return mod?.startsWith('M') ? 'Math' : 'RW'
}

function normalizeAssignmentType(type?: string | null): string {
  if (!type || type === 'evaluate') return 'mock'
  return type
}

function QuestionImages({ images }: { images: string[] }) {
  if (!images || images.length === 0) return null
  return (
    <>
      {images.map((src, idx) => {
        const imgSrc = getImageSrc(src)
        if (!imgSrc) return null
        return (
          <Box key={idx} my={8} style={{ textAlign: 'center' }}>
            <img src={imgSrc} alt={`Figure ${idx + 1}`}
              style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid var(--sat-border-soft)' }} />
          </Box>
        )
      })}
    </>
  )
}

function QuestionImage({ src }: { src: string | null }) {
  if (!src) return null
  const imgSrc = getImageSrc(src)
  if (!imgSrc) return null
  return (
    <Box my={8} style={{ textAlign: 'center' }}>
      <img src={imgSrc} alt="Question figure" style={{ maxWidth: '100%', borderRadius: 8 }} />
    </Box>
  )
}

function ChoiceContent({ text }: { text: string }) {
  if (!text) return null
  const imageSrc = getImageSrc(text)
  if (imageSrc) {
    return <img src={imageSrc} alt="Choice" style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 4 }} />
  }
  return <RichText size="sm">{text}</RichText>
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                      */
/* ------------------------------------------------------------------ */
type Phase = 'loading' | 'error' | 'test' | 'break' | 'confirm-submit' | 'summary'

export function SatEvaluation() {
  const { uuid } = useParams<{ uuid: string; templateId: string }>()

  const [phase, setPhase] = useState<Phase>('loading')
  const [errorMsg, setErrorMsg] = useState('')
  const [evaluationId, setEvaluationId] = useState<number | null>(null)
  const [allQuestions, setAllQuestions] = useState<EvalQuestionPayload[]>([])
  const [answers, setAnswers] = useState<Record<number, string>>({}) // detail_id -> answer
  const [currentIdx, setCurrentIdx] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [summary, setSummary] = useState<EvalSummaryResponse | null>(null)
  const [completedModules, setCompletedModules] = useState<Set<string>>(new Set())
  const [assignmentType, setAssignmentType] = useState<string>('mock')

  // Timer
  const [timerSeconds, setTimerSeconds] = useState(RW_MINUTES * 60)
  const [breakSeconds, setBreakSeconds] = useState(BREAK_SECONDS)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Derived
  const isPractice = PRACTICE_TYPES.includes(assignmentType)
  const currentQ = allQuestions[currentIdx] || null
  const currentQa = currentQ?.question_answers?.[0]
  const currentModule = currentQ?.module || ''
  const currentSection = sectionOf(currentModule)
  const displayPassage = stripLeadingQuestionNumber(currentQ?.question_text)
  const displayQuestion = stripLeadingQuestionNumber(currentQa?.question)

  // Module boundaries: indices of first question per module
  const moduleRanges = useRef<Record<string, { start: number; end: number }>>({})

  function computeModuleRanges(questions: EvalQuestionPayload[]) {
    const ranges: Record<string, { start: number; end: number }> = {}
    questions.forEach((q, i) => {
      const mod = q.module || ''
      if (!ranges[mod]) ranges[mod] = { start: i, end: i }
      else ranges[mod].end = i
    })
    moduleRanges.current = ranges
  }

  /* ---- Prepare ---- */
  useEffect(() => {
    if (!uuid) { setPhase('error'); setErrorMsg('Invalid evaluation URL.'); return }

    prepareEvaluation(uuid)
      .then((res) => {
        const d = res.data
        setEvaluationId(d.evaluation_id)
        setAllQuestions(d.questions)
        setAssignmentType(normalizeAssignmentType(d.assignment_type))
        computeModuleRanges(d.questions)
        setPhase('test')

        // Restore saved answers on re-entry
        if (d.saved_answers && Object.keys(d.saved_answers).length > 0) {
          const restored: Record<number, string> = {}
          for (const [k, v] of Object.entries(d.saved_answers)) restored[Number(k)] = v
          setAnswers(restored)
          // Jump to first unanswered
          const firstUnanswered = d.questions.findIndex((q) => !(Number(q.detail_id) in restored))
          if (firstUnanswered >= 0) setCurrentIdx(firstUnanswered)
        }

        // Set timer based on first question section (only for mock type)
        const atype = normalizeAssignmentType(d.assignment_type)
        if (!PRACTICE_TYPES.includes(atype)) {
          const firstMod = d.questions[0]?.module || ''
          if (firstMod.startsWith('M')) setTimerSeconds(MATH_MINUTES * 60)
          else setTimerSeconds(RW_MINUTES * 60)
        }
      })
      .catch((err) => { setPhase('error'); setErrorMsg(err.response?.data?.detail || 'Failed to prepare evaluation.') })
  }, [uuid])

  /* ---- Timer (only for mock type) ---- */
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    if (isPractice) return // No timer for practice types

    if (phase === 'test') {
      timerRef.current = setInterval(() => setTimerSeconds((p) => (p <= 1 ? 0 : p - 1)), 1000)
    } else if (phase === 'break') {
      timerRef.current = setInterval(() => {
        setBreakSeconds((p) => {
          if (p <= 1) { setPhase('test'); setTimerSeconds(MATH_MINUTES * 60); return 0 }
          return p - 1
        })
      }, 1000)
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [phase, isPractice])

  /* ---- Navigation ---- */
  function canGoBack() {
    if (isPractice) return currentIdx > 0
    if (currentIdx <= 0) return false
    const prevQ = allQuestions[currentIdx - 1]
    return prevQ.module === currentModule
  }

  function canGoForward() {
    return currentIdx < allQuestions.length - 1
  }

  function isLastInModule() {
    if (isPractice) return currentIdx >= allQuestions.length - 1
    const range = moduleRanges.current[currentModule]
    return range ? currentIdx >= range.end : true
  }

  function goBack() {
    if (canGoBack()) setCurrentIdx(currentIdx - 1)
  }

  function goNext() {
    if (isPractice) {
      if (currentIdx >= allQuestions.length - 1) {
        // Last question — go to confirm submit
        setPhase('confirm-submit')
      } else {
        setCurrentIdx(currentIdx + 1)
      }
      return
    }

    if (isLastInModule()) {
      // End of module — flush answers to server, then move to next module or end
      flushAnswersAndAdvance()
    } else if (canGoForward()) {
      setCurrentIdx(currentIdx + 1)
    }
  }

  async function flushAnswersAndAdvance() {
    // Bulk save answers for current module
    const range = moduleRanges.current[currentModule]
    if (range) {
      const moduleQs = allQuestions.slice(range.start, range.end + 1)
      const toSave = moduleQs
        .filter((q) => answers[q.detail_id])
        .map((q) => ({ detail_id: q.detail_id, user_answer: answers[q.detail_id] }))
      if (toSave.length > 0) {
        try { await submitEvalAnswersBulk(toSave) } catch { /* will retry on final submit */ }
      }
    }

    setCompletedModules((prev) => new Set([...prev, currentModule]))

    // For Module 1 endings (RW-1, M-1): call complete-module to generate adaptive Module 2
    const isModule1 = currentModule === 'RW-1' || currentModule === 'M-1'
    if (isModule1 && evaluationId) {
      try {
        const res = await completeModule(evaluationId, currentModule)
        const newQuestions = res.data.questions || []
        if (newQuestions.length > 0) {
          // Append new questions and recompute module ranges
          const updated = [...allQuestions, ...newQuestions]
          setAllQuestions(updated)
          computeModuleRanges(updated)

          // Navigate to first new question
          const nextIdx = allQuestions.length // index of first new question in updated array
          const nextModule = updated[nextIdx]?.module || ''

          // RW -> Math transition = break (RW-2 just ended? No — this is after RW-1, next is RW-2)
          if (sectionOf(currentModule) === 'RW' && sectionOf(nextModule) === 'Math') {
            setCurrentIdx(nextIdx)
            setPhase('break')
            setBreakSeconds(BREAK_SECONDS)
          } else {
            setCurrentIdx(nextIdx)
          }
          return
        }
      } catch {
        notifications.show({ title: 'Error', message: 'Failed to generate next module questions.', color: 'red' })
      }
    }

    // For Module 2 endings (RW-2, M-2) or fallback: check if there's a next module
    const nextIdx = (moduleRanges.current[currentModule]?.end ?? currentIdx) + 1
    if (nextIdx >= allQuestions.length) {
      // Last module done — go to confirm
      setPhase('confirm-submit')
      return
    }

    const nextModule = allQuestions[nextIdx].module || ''
    // RW -> Math transition = break
    if (sectionOf(currentModule) === 'RW' && sectionOf(nextModule) === 'Math') {
      setCurrentIdx(nextIdx)
      setPhase('break')
      setBreakSeconds(BREAK_SECONDS)
    } else {
      setCurrentIdx(nextIdx)
    }
  }

  /* ---- Answer tracking ---- */
  function setAnswer(detailId: number, value: string) {
    setAnswers((prev) => ({ ...prev, [detailId]: value }))
  }

  function clearAnswer(detailId: number) {
    setAnswers((prev) => { const n = { ...prev }; delete n[detailId]; return n })
  }

  /* ---- Unanswered questions ---- */
  function getUnanswered(): { sequence: number; module: string | null }[] {
    return allQuestions.filter((q) => !answers[q.detail_id]).map((q) => ({ sequence: q.sequence, module: q.module }))
  }

  /* ---- Final submit ---- */
  const handleFinalSubmit = useCallback(async () => {
    if (!evaluationId) return
    setSubmitting(true)
    try {
      // Flush all remaining answers
      const toSave = allQuestions
        .filter((q) => answers[q.detail_id])
        .map((q) => ({ detail_id: q.detail_id, user_answer: answers[q.detail_id] }))
      if (toSave.length > 0) {
        await submitEvalAnswersBulk(toSave)
      }
      const res = await submitEvaluation(evaluationId)
      setSummary(res.data)
      setPhase('summary')
      if (timerRef.current) clearInterval(timerRef.current)
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to submit evaluation.', color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }, [evaluationId, allQuestions, answers])

  function skipBreak() {
    setPhase('test')
    setTimerSeconds(MATH_MINUTES * 60)
  }

  /* ================================================================ */
  /*  RENDER                                                           */
  /* ================================================================ */

  if (phase === 'loading') {
    return (
      <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Loader color="eveningSea" size="xl" />
        <Text mt="lg" size="lg" fw={600} c="var(--sat-text)">
          {isPractice ? 'Preparing your questions...' : 'Your mock test is preparing. Please wait...'}
        </Text>
      </Box>
    )
  }

  if (phase === 'error') {
    return (
      <Container size="sm" py="xl">
        <Paper radius="xl" p="xl" withBorder style={{ background: 'var(--sat-surface)', textAlign: 'center' }}>
          <AlertTriangle size={48} color="var(--sat-red)" style={{ margin: '0 auto 12px' }} />
          <Title order={3} c="var(--sat-text)" mb="sm">Could not start</Title>
          <Text c="var(--sat-text-muted)">{errorMsg}</Text>
        </Paper>
      </Container>
    )
  }

  // Break screen (mock type only — never shown for practice)
  if (phase === 'break' && !isPractice) {
    return (
      <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Paper radius="xl" p="xl" withBorder style={{ background: 'var(--sat-surface)', textAlign: 'center', maxWidth: 500 }}>
          <Coffee size={64} color="var(--sat-green)" style={{ margin: '0 auto 16px' }} />
          <Title order={2} c="var(--sat-text)" mb="sm">Break Time</Title>
          <Text c="var(--sat-text-muted)" mb="lg">
            You have completed the Reading &amp; Writing section. Take a 10-minute break before starting the Math section.
          </Text>
          <Text fz={48} fw={800} c="var(--sat-text)" mb="lg">{formatTime(breakSeconds)}</Text>
          <Button color="grapefruit" radius="xl" size="lg" onClick={skipBreak}>Skip Break &amp; Start Math</Button>
        </Paper>
      </Box>
    )
  }

  // Confirm submit with unanswered warning
  if (phase === 'confirm-submit') {
    const unanswered = getUnanswered()
    return (
      <Container size="sm" py="xl">
        <Paper radius="xl" p="xl" withBorder style={{ background: 'var(--sat-surface)' }}>
          <Title order={2} c="var(--sat-text)" mb="md" ta="center">
            Submit {isPractice ? PRACTICE_LABEL[assignmentType] || 'Practice' : 'Mock'}
          </Title>

          {unanswered.length > 0 ? (
            <>
              <Paper p="md" radius="md" mb="lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid var(--sat-red)' }}>
                <Group gap="sm" mb="sm">
                  <AlertTriangle size={20} color="var(--sat-red)" />
                  <Text fw={700} c="var(--sat-red)">{unanswered.length} question(s) not answered</Text>
                </Group>
                <Box style={{ maxHeight: 200, overflow: 'auto' }}>
                  <List size="sm" c="var(--sat-text-muted)">
                    {unanswered.map((u) => (
                      <List.Item key={u.sequence}>
                        Question #{u.sequence}{u.module ? ` (${u.module})` : ''}
                      </List.Item>
                    ))}
                  </List>
                </Box>
              </Paper>
              <Group justify="center" gap="md">
                <Button variant="light" radius="xl" size="lg" onClick={() => {
                  // Go back to first unanswered
                  const firstIdx = allQuestions.findIndex((q) => !answers[q.detail_id])
                  if (firstIdx >= 0) { setCurrentIdx(firstIdx); setPhase('test') }
                }}>
                  Go Back &amp; Answer
                </Button>
                <Button color="grapefruit" radius="xl" size="lg" onClick={handleFinalSubmit} loading={submitting}>
                  Submit Anyway
                </Button>
              </Group>
            </>
          ) : (
            <>
              <Text c="var(--sat-text-muted)" ta="center" mb="lg">
                <CheckCircle2 size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                All {allQuestions.length} questions answered. Ready to submit?
              </Text>
              <Group justify="center">
                <Button color="grapefruit" radius="xl" size="lg" onClick={handleFinalSubmit} loading={submitting}>
                  Submit {isPractice ? PRACTICE_LABEL[assignmentType] || 'Practice' : 'Mock'}
                </Button>
              </Group>
            </>
          )}
        </Paper>
      </Container>
    )
  }

  // Summary
  if (phase === 'summary' && summary) {
    return (
      <Container size="lg" py="xl">
        <EvaluateResult
          title={isPractice ? `${PRACTICE_LABEL[assignmentType] || 'Practice'} Complete` : 'Mock Complete'}
          rwScore={summary.rw_score}
          mathScore={summary.math_score}
          totalScore={summary.total_score}
          totalQuestions={allQuestions.length}
          masteryResults={summary.mastery_results}
          isPractice={isPractice}
        />
      </Container>
    )
  }

  // Test phase
  if (phase === 'test' && currentQ) {
    const qa = currentQa
    const isSpr = qa?.question_type === 'spr'
    const currentAnswer = answers[currentQ.detail_id] || ''
    const answeredCount = allQuestions.filter((q) => answers[q.detail_id]).length
    const progressPct = allQuestions.length > 0 ? Math.round((answeredCount / allQuestions.length) * 100) : 0

    // Question nav dots — for practice mode show all questions, for mock show current module
    const range = moduleRanges.current[currentModule]
    const navQuestions = isPractice ? allQuestions : (range ? allQuestions.slice(range.start, range.end + 1) : [])
    const navOffset = isPractice ? 0 : (range?.start ?? 0)

    return (
      <Container size="lg" py="md">
        <Stack gap="md">
          {/* Header */}
          <Paper radius="xl" p="md" withBorder style={{ background: 'var(--sat-surface)' }}>
            <Group justify="space-between" wrap="wrap">
              <Group gap="sm">
                {isPractice ? (
                  <Badge size="lg" radius="xl" color="grapefruit" variant="light">
                    {PRACTICE_LABEL[assignmentType] || 'Practice'}
                  </Badge>
                ) : (
                  <>
                    <Badge size="lg" radius="xl" color={currentSection === 'RW' ? 'blue' : 'grape'} variant="light">
                      {currentSection === 'RW' ? 'Reading & Writing' : 'Math'}
                    </Badge>
                    <Text size="sm" c="var(--sat-text-muted)">Module {currentModule}</Text>
                  </>
                )}
              </Group>

              {/* Timer — only for mock type */}
              {!isPractice && (
                <Group gap="sm">
                  <Clock size={18} color="var(--sat-text-muted)" />
                  <Text fz={22} fw={800} c={timerSeconds < 300 ? 'var(--sat-red)' : 'var(--sat-text)'} style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatTime(timerSeconds)}
                  </Text>
                </Group>
              )}

              <Box style={{ minWidth: 180 }}>
                <Group justify="space-between" mb={4}>
                  <Text size="xs" c="var(--sat-text-muted)">Q {currentQ.sequence} / {allQuestions.length}</Text>
                  <Text size="xs" fw={600} c="var(--sat-text)">{progressPct}%</Text>
                </Group>
                <Progress value={progressPct} size="sm" radius="xl" color="grapefruit" />
              </Box>
            </Group>

            {/* Question nav dots */}
            <Group gap={4} mt="sm" justify="center" wrap="wrap">
              {navQuestions.map((q, i) => {
                const idx = navOffset + i
                const isActive = idx === currentIdx
                const isAnswered = !!answers[q.detail_id]
                return (
                  <Box
                    key={q.detail_id}
                    onClick={() => {
                      // In mock mode, can only navigate within the current module
                      if (!isPractice) {
                        const qMod = allQuestions[idx]?.module || ''
                        if (qMod !== currentModule) return
                      }
                      setCurrentIdx(idx)
                    }}
                    style={{
                      width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
                      background: isActive ? 'var(--sat-red)' : isAnswered ? 'var(--sat-green)' : 'var(--sat-surface-dim)',
                      color: isActive || isAnswered ? '#fff' : 'var(--sat-text-muted)',
                      border: isActive ? 'none' : '1px solid var(--sat-border, #444)',
                    }}
                  >
                    {i + 1}
                  </Box>
                )
              })}
            </Group>
          </Paper>

          {/* Question */}
          {qa && (
            <Paper radius="xl" p="xl" withBorder style={{ background: 'var(--sat-surface)' }}>
              <Stack gap="md">
                {displayPassage && displayPassage !== displayQuestion && (
                  <Paper p="md" radius="md" style={{ background: 'var(--sat-surface-dim)', maxHeight: 300, overflow: 'auto' }}>
                    <RichText size="sm">{displayPassage}</RichText>
                  </Paper>
                )}
                <QuestionImages images={currentQ.question_images} />
                <Box>
                  <RichText size="md">{displayQuestion}</RichText>
                  <QuestionImage src={qa.question_image} />
                </Box>

                {isSpr ? (
                  <TextInput
                    label="Your Answer"
                    placeholder="Type your answer..."
                    value={currentAnswer}
                    onChange={(e) => setAnswer(currentQ.detail_id, e.currentTarget.value)}
                    size="lg" radius="md"
                  />
                ) : (
                  <Stack gap="xs">
                    {qa.choices.sort((a, b) => a.display_order - b.display_order).map((choice) => {
                      const isSelected = currentAnswer === choice.choice_label
                      return (
                        <Paper
                          key={choice.id} p="sm" radius="md"
                          onClick={() => {
                            if (isSelected) clearAnswer(currentQ.detail_id)
                            else setAnswer(currentQ.detail_id, choice.choice_label)
                          }}
                          style={{
                            cursor: 'pointer',
                            border: isSelected ? '2px solid var(--sat-green)' : '1px solid var(--sat-border, #444)',
                            background: isSelected ? 'rgba(34, 197, 94, 0.08)' : 'var(--sat-surface-dim)',
                            transition: 'all 0.15s',
                          }}
                        >
                          <Group gap="sm" wrap="nowrap">
                            <Badge circle size="lg" color={isSelected ? 'green' : 'gray'} variant={isSelected ? 'filled' : 'light'}>
                              {choice.choice_label}
                            </Badge>
                            <ChoiceContent text={choice.choice_text} />
                          </Group>
                        </Paper>
                      )
                    })}
                  </Stack>
                )}

                {/* Navigation */}
                <Group justify="space-between" mt="md">
                  <Button
                    variant="light" radius="xl" size="lg"
                    leftSection={<ArrowLeft size={18} />}
                    disabled={!canGoBack()}
                    onClick={goBack}
                  >
                    Back
                  </Button>
                  <Button
                    color="grapefruit" radius="xl" size="lg"
                    rightSection={<ArrowRight size={18} />}
                    onClick={goNext}
                    loading={submitting}
                  >
                    {isPractice
                      ? (currentIdx >= allQuestions.length - 1 ? 'Finish' : 'Next')
                      : (isLastInModule() ? (canGoForward() ? 'Next Module' : 'Finish') : 'Next')
                    }
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Container>
    )
  }

  return null
}
